import type { Theme as MuiTheme, SxProps } from "@mui/material";
import type { ArchivedRecordsSwitch } from "~/helpers/grid/archivedRecordsScope";

import { alpha, Box, GlobalStyles, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	CellClassParams,
	ColDef,
	ColumnState,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	GridOptions,
	GridReadyEvent,
	iconSetMaterial,
	MenuItemDef,
	Theme,
	themeBalham,
	ValueFormatterParams
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import "~/components/grid/agGridModules";

import checkCondition from "~/components/grid/conditionalFormatting/checkCondition";
import FormattingPanel from "~/components/grid/conditionalFormatting/FormattingPanel";
import emptyStateOverlayOptions from "~/components/grid/emptyStateOverlayOptions";
import DefaultServerSideFileExport from "~/components/grid/export/DefaultServerSideFileExport";
import GridEmptyState from "~/components/grid/GridEmptyState";
import GridLoadingOverlay from "~/components/grid/GridLoadingOverlay";
import GridLockOverlay from "~/components/grid/GridLockOverlay";
import {
	gridContainerSx,
	gridFilterBarContainerSx,
	gridFilterBarInnerSx,
	gridFullscreenInnerSx,
	gridInnerSx
} from "~/components/grid/gridLayoutStyles";
import MruFilterBar from "~/components/grid/MruFilterBar";
import ExportImportBox from "~/components/grid/ribbonComponents/ExportImportBox";
import GridRowCountStatusPanel from "~/components/grid/statusPanels/GridRowCountStatusPanel";
import useAgGridLocaleText from "~/components/grid/useAgGridLocaleText";
import useGridColumnCapabilities from "~/components/grid/useGridColumnCapabilities";
import useGridExternalFilters, { DEFAULT_ROW_SELECTION } from "~/components/grid/useGridExternalFilters";
import useGridFilterFeedback from "~/components/grid/useGridFilterFeedback";
import useReactiveGridOptions from "~/components/grid/useReactiveGridOptions";
import { formatCurrencyCell } from "~/helpers/grid/coerceCellNumber";
import { routeExportMenuItems } from "~/helpers/grid/contextMenuDefaultItems";
import { DEFAULT_GRID_COMPARATOR } from "~/helpers/grid/defaultGridComparator";
import guardGetRowIdAgainstStubRows from "~/helpers/grid/guardGetRowIdAgainstStubRows";
import guardSetFilterFormatters from "~/helpers/grid/guardSetFilterFormatters";
import { isServerSideRowModel } from "~/helpers/grid/isServerSideRowModel";
import overriddenCellGlobalStyle, { OVERRIDDEN_CELL_CLASS } from "~/helpers/grid/overriddenCellStyling";
import { getSsrmExportSource } from "~/helpers/grid/ssrmExportSource";
import SSRM_GRID_DEFAULTS, { SSRM_STATUS_BAR } from "~/helpers/grid/ssrmGridDefaults";
import { toolPanelsDefault } from "~/helpers/grid/toolPanels";
import useGridFilterBar from "~/hooks/useGridFilterBar";
import AppThemeContext from "~/providers/AppThemeContext";
import GridFileExportContext from "~/providers/GridFileExportContext";

const LATE_READY_CELL_TINT_ALPHA = 0.15;
const EMBEDDED_ROW_SELECTION = { ...DEFAULT_ROW_SELECTION, checkboxes: false };

interface AgGridWithoutRibbonProps<TData> {
	archivedRecordsSwitch?: ArchivedRecordsSwitch;
	classSuffix?: string;
	embedded?: boolean;
	externalFormattingTrigger?: React.MutableRefObject<(() => void) | null>;
	getRowId: (params: { data: TData }) => string;
	gridDataKey: string;
	gridOptions?: GridOptions;
	formattingRules?: GridFormattingRule[];
	fullScreen?: boolean;
	fullScreenRibbon?: React.ReactNode;
	gridRef: React.RefObject<AgGridReact<TData>>;
	isProcessing?: boolean;
	isError?: boolean;
	onRetry?: () => void;
	/** Opt-in: when a global-search quick filter empties this grid, show a "No results" state with a clear-search CTA. */
	quickFilterEmptyState?: boolean;
	rowData?: TData[];
	showOnlySelected?: boolean;
	suppressStatePersistence?: boolean;
	transformLoadedColumnState?: (state: ColumnState[], liveColIds: ReadonlySet<string>) => ColumnState[];
}

const AgGridWithoutRibbonContent = <TData,>({
	archivedRecordsSwitch,
	classSuffix,
	embedded = false,
	externalFormattingTrigger,
	formattingRules: controlledFormattingRules,
	fullScreen = false,
	fullScreenRibbon,
	getRowId,
	gridDataKey,
	gridOptions,
	gridRef,
	isError = false,
	isProcessing = false,
	onRetry,
	quickFilterEmptyState = false,
	rowData,
	showDefaultExport = false,
	showOnlySelected = false,
	suppressStatePersistence = false,
	transformLoadedColumnState,
	...allNativeProps
}: AgGridWithoutRibbonProps<TData> & { showDefaultExport?: boolean } & Partial<
		React.ComponentProps<typeof AgGridReact<TData>>
	>) => {
	const { getContextMenuItems: propGetContextMenuItems, ...nativeProps } = allNativeProps;
	const { themeSize } = useContext(AppThemeContext);
	const fileExport = useContext(GridFileExportContext);
	const muiTheme = useTheme();
	const localeText = useAgGridLocaleText();
	const { filterBarPlacement, mruFilters, recordFilterChange, setFiltersPaused, showFilterBar, updateFilterModel } =
		useGridFilterBar(gridDataKey, gridRef);
	const [ownFormattingRules, setFormattingRules] = useState<GridFormattingRule[]>([]);
	const formattingRules = controlledFormattingRules ?? ownFormattingRules;
	const [formattingPanelOpen, setFormattingPanelOpen] = useState(false);
	const formattingRulesRef = useRef<GridFormattingRule[]>([]);
	const [gridStateRestored, setGridStateRestored] = useState(false);

	useEffect(() => {
		formattingRulesRef.current = formattingRules;
		const api = gridRef.current?.api;
		if (!api || api.isDestroyed()) return;
		api.refreshCells({ force: true });
		api.redrawRows();
	}, [formattingRules, gridRef]);

	// Expose formatting trigger to parent if provided
	useEffect(() => {
		if (externalFormattingTrigger) {
			externalFormattingTrigger.current = () => setFormattingPanelOpen(true);
		}
		return () => {
			if (externalFormattingTrigger) {
				externalFormattingTrigger.current = null;
			}
		};
	}, [externalFormattingTrigger]);

	const gridStateRestoredRef = useRef(false);
	const gridStateRestoreTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		gridStateRestoredRef.current = gridStateRestored;
	}, [gridStateRestored]);

	const ownApiRef = useRef<GridApi<TData> | null>(null);

	const saveGridState = useCallback(() => {
		if (suppressStatePersistence) return;
		const api = ownApiRef.current;
		if (api && !api.isDestroyed() && gridStateRestoredRef.current) {
			const columnState = api.getColumnState();
			const filterModel = api.getFilterModel();
			const selectedRows = api.getSelectedNodes().map((node) => node.id);
			const focusedCell = api.getFocusedCell();
			const focusedRowNode = focusedCell ? api.getDisplayedRowAtIndex(focusedCell.rowIndex) : undefined;
			const focusedRowId = focusedRowNode ? focusedRowNode.id : null;
			const focusedColumn = focusedCell ? focusedCell.column.getColId() : null;
			const firstVisibleRowIndex = api.getFirstDisplayedRowIndex();
			const firstVisibleColumn = api.getAllDisplayedColumns()[0]?.getColId();
			const state = {
				columnState,
				filterModel,
				firstVisibleColumn,
				firstVisibleRowIndex,
				focusedColumn,
				focusedRowId,
				selectedRows
			};
			localStorage.setItem("gridstate-" + gridDataKey, JSON.stringify(state));
		}
	}, [gridDataKey, suppressStatePersistence]);

	const loadGridState = useCallback(() => {
		if (suppressStatePersistence) return;
		const savedState = localStorage.getItem("gridstate-" + gridDataKey);
		const api = ownApiRef.current;
		if (savedState && api && !api.isDestroyed()) {
			const state = JSON.parse(savedState);
			const liveColIds = new Set((api.getColumns() ?? []).map((column) => column.getColId()));
			api.applyColumnState({
				applyOrder: true,
				state: transformLoadedColumnState
					? transformLoadedColumnState(state.columnState, liveColIds)
					: state.columnState
			});
			api.setFilterModel(state.filterModel);
			state.selectedRows.forEach((id: string) => {
				const node = api.getRowNode(id);
				if (node) node.setSelected(true);
			});
			if (state.focusedRowId && state.focusedColumn) {
				const node = api.getRowNode(state.focusedRowId);
				if (node && node.rowIndex !== null) {
					api.setFocusedCell(node.rowIndex, state.focusedColumn);
					api.ensureIndexVisible(node.rowIndex, "middle");
					api.ensureColumnVisible(state.focusedColumn);
				} else {
					if (state.firstVisibleRowIndex !== undefined) {
						api.ensureIndexVisible(state.firstVisibleRowIndex, "top");
					}
					if (state.firstVisibleColumn) {
						api.ensureColumnVisible(state.firstVisibleColumn);
					}
				}
			} else {
				if (state.firstVisibleRowIndex !== undefined) {
					api.ensureIndexVisible(state.firstVisibleRowIndex, "top");
				}
				if (state.firstVisibleColumn) {
					api.ensureColumnVisible(state.firstVisibleColumn);
				}
			}
		}

		gridStateRestoreTimer.current = setTimeout(() => setGridStateRestored(true), 3000);
	}, [gridDataKey, suppressStatePersistence, transformLoadedColumnState]);

	const debouncedSaveGridState = useMemo(() => debounce(saveGridState, 500), [saveGridState]);

	useEffect(() => {
		return () => {
			saveGridState();
		};
	}, [saveGridState]);

	// A restore timer left pending at unmount calls setState after the host environment is gone.
	useEffect(() => () => clearTimeout(gridStateRestoreTimer.current), []);

	const defaultGridOptions: GridOptions<TData> = useMemo(() => {
		return {
			columnDefs: [],
			columnTypes: {
				currency: {
					valueFormatter: (p: ValueFormatterParams) => formatCurrencyCell(p.value),
					width: 150
				}
			},
			defaultColDef: {
				cellClassRules: {
					"grid-cell-editable": (params) => !!params.colDef?.editable
				},
				cellStyle: (params: CellClassParams) => {
					const currentRules = formattingRulesRef.current;
					const rulesForColumn = currentRules.filter(
						(rule) => rule.conditions[0].column === params.colDef.field && rule.active && !rule.applyToRow
					);
					for (const rule of rulesForColumn) {
						const styling = checkCondition(params, rule);
						if (styling) return styling;
					}
				},
				filter: true,
				flex: 1,
				minWidth: 120,
				suppressHeaderMenuButton: true
			},
			getRowStyle: (params) => {
				const currentRules = formattingRulesRef.current;
				const rulesForRow = currentRules.filter((rule) => rule.applyToRow && rule.active);
				for (const rule of rulesForRow) {
					const styling = checkCondition(params, rule);
					if (styling) {
						return styling;
					}
				}
			},
			loadingOverlayComponent: GridLoadingOverlay,
			noRowsOverlayComponent: GridEmptyState,
			rowGroupPanelShow: "onlyWhenGrouping",
			sideBar: {
				toolPanels: toolPanelsDefault
			},
			statusBar: {
				statusPanels: [
					{ align: "left", statusPanel: GridRowCountStatusPanel },
					{ align: "center", statusPanel: "agAggregationComponent" }
				]
			},
			undoRedoCellEditing: true,
			undoRedoCellEditingLimit: 100
		};
	}, []);

	const serverSideRowModel = isServerSideRowModel(nativeProps.rowModelType, gridOptions);
	const { doesExternalFilterPass, isExternalFilterPresent, rowSelection } = useGridExternalFilters<TData>({
		archivedRecordsSwitch,
		gridOptions,
		gridRef,
		isServerSide: serverSideRowModel,
		rowSelection: nativeProps.rowSelection ?? (embedded ? EMBEDDED_ROW_SELECTION : DEFAULT_ROW_SELECTION),
		showOnlySelected
	});

	const customGetContextMenuItems = propGetContextMenuItems ?? gridOptions?.getContextMenuItems;
	const mergedGetContextMenuItems = useMemo(() => {
		if (!serverSideRowModel) return customGetContextMenuItems;
		return (params: GetContextMenuItemsParams<TData>) =>
			routeExportMenuItems(
				customGetContextMenuItems
					? (customGetContextMenuItems(params as GetContextMenuItemsParams) as (DefaultMenuItem | MenuItemDef)[])
					: [...(params.defaultItems ?? [])],
				{ fileExport: fileExport ?? undefined, isServerSide: true }
			);
	}, [customGetContextMenuItems, fileExport, serverSideRowModel]);

	const baseGridOptions = {
		...defaultGridOptions,
		...(serverSideRowModel ? SSRM_GRID_DEFAULTS : undefined),
		...(serverSideRowModel && !gridOptions?.statusBar ? { statusBar: SSRM_STATUS_BAR } : undefined),
		...gridOptions
	};
	const filterFeedback = useGridFilterFeedback();
	const { apply: applyColumnCapabilities, hasAdjustedColumnDefs: hasColumnCapabilities } = useGridColumnCapabilities(
		gridRef,
		serverSideRowModel
	);
	const guardedNativeColumnDefs = useMemo(
		() => applyColumnCapabilities(guardSetFilterFormatters(nativeProps.columnDefs ?? [])),
		[applyColumnCapabilities, nativeProps.columnDefs]
	);
	const guardedColumnDefs = useMemo(
		() => applyColumnCapabilities(guardSetFilterFormatters(baseGridOptions.columnDefs ?? [])),
		[applyColumnCapabilities, baseGridOptions.columnDefs]
	);
	const combinedGridOptions: GridOptions<TData> = {
		...baseGridOptions,
		defaultColDef: { comparator: DEFAULT_GRID_COMPARATOR, ...baseGridOptions.defaultColDef },
		columnDefs: guardedColumnDefs,
		...(mergedGetContextMenuItems ? { getContextMenuItems: mergedGetContextMenuItems } : {}),
		...emptyStateOverlayOptions(baseGridOptions, {
			...baseGridOptions.noRowsOverlayComponentParams,
			isError,
			onRetry,
			quickFilterEmptyState
		})
	};

	const applyLatestGridOptions = useReactiveGridOptions({
		gridDataKey,
		gridOptions,
		gridRef,
		wrapperResolvedOptions: {
			defaultColDef: combinedGridOptions.defaultColDef,
			getContextMenuItems: mergedGetContextMenuItems,
			noRowsOverlayComponentParams: combinedGridOptions.noRowsOverlayComponentParams,
			overlayComponentParams: combinedGridOptions.overlayComponentParams
		}
	});

	const consumerOnGridReady = nativeProps.onGridReady ?? gridOptions?.onGridReady;
	const mergedOnGridReady = useCallback(
		(event: GridReadyEvent<TData>) => {
			ownApiRef.current = event.api;
			filterFeedback.setApi(event.api);
			applyLatestGridOptions(event.api);
			consumerOnGridReady?.(event);
			updateFilterModel();
		},
		[applyLatestGridOptions, consumerOnGridReady, filterFeedback.setApi, updateFilterModel]
	);

	// AG Grid gives a direct prop precedence over the same gridOptions key, so the consumer's handler runs here.
	const mergedOnFilterChanged = useCallback(() => {
		recordFilterChange();
		saveGridState();
		(gridOptions?.onFilterChanged as (() => void) | undefined)?.();
	}, [gridOptions, recordFilterChange, saveGridState]);

	const getStubSafeRowId = useMemo(() => guardGetRowIdAgainstStubRows(getRowId), [getRowId]);

	const myTheme = themeBalham.withPart(iconSetMaterial).withParams({
		fontSize: Number(themeSize),
		headerRowBorder: true,
		wrapperBorder: false,
		...(embedded && {
			backgroundColor: "transparent",
			headerBackgroundColor: "transparent",
			headerHeight: 36,
			oddRowBackgroundColor: "transparent",
			rowHeight: 32
		})
	});

	const theme = useMemo<Theme | "legacy">(() => {
		return myTheme;
	}, [myTheme]);

	const layoutClassSuffix = fullScreen ? undefined : classSuffix;
	const shouldRenderDefaultExport = showDefaultExport && !!fileExport;
	const shouldStackControlsWithGrid = showFilterBar || shouldRenderDefaultExport;
	const containerSxArray = [
		fullScreen ? gridContainerSx.fullscreen : gridContainerSx.normal,
		...(gridOptions?.domLayout === "autoHeight" ? [gridContainerSx.autoheight] : []),
		...(layoutClassSuffix && layoutClassSuffix in gridContainerSx
			? [gridContainerSx[layoutClassSuffix as keyof typeof gridContainerSx]]
			: []),
		...(shouldStackControlsWithGrid ? [gridFilterBarContainerSx] : []),
		fullScreen
			? { backgroundColor: (theme: MuiTheme) => theme.palette.background.paper }
			: {
					backgroundColor: (theme: MuiTheme) => (embedded ? "transparent" : theme.palette.background.paper),
					position: "relative"
				}
	] as SxProps<MuiTheme>;

	const innerSxArray = [
		fullScreen ? gridFullscreenInnerSx : gridInnerSx.normal,
		...(layoutClassSuffix && layoutClassSuffix in gridInnerSx
			? [gridInnerSx[layoutClassSuffix as keyof typeof gridInnerSx]]
			: []),
		...(shouldStackControlsWithGrid ? [gridFilterBarInnerSx(gridOptions?.domLayout === "autoHeight")] : []),
		{ position: "relative" }
	] as SxProps<MuiTheme>;

	return (
		<Box component={embedded || classSuffix === "tab" ? "div" : Paper} sx={containerSxArray}>
			{fullScreen && fullScreenRibbon}
			{shouldRenderDefaultExport && (
				<Box sx={{ alignSelf: "flex-start", px: 1 }}>
					<ExportImportBox hideImport />
				</Box>
			)}
			<GlobalStyles
				styles={{
					".ag-number-field-input": {
						textAlign: "right"
					},
					".grid-cell-editable": {
						backgroundColor: "#2244cc25"
					},
					".grid-cell-edited": {
						backgroundColor: "#44cc2225"
					},
					".grid-cell-late-ready": {
						backgroundColor: alpha(muiTheme.palette.warning.main, LATE_READY_CELL_TINT_ALPHA)
					},
					[`.${OVERRIDDEN_CELL_CLASS}`]: overriddenCellGlobalStyle(muiTheme)
				}}
			/>
			{showFilterBar && filterBarPlacement === "top" && (
				<MruFilterBar
					hasRibbon={false}
					columnDefs={(gridOptions?.columnDefs ?? []) as ColDef[]}
					gridRef={gridRef}
					mruFilters={mruFilters}
					onFilterApplied={updateFilterModel}
					onPausedChange={setFiltersPaused}
					pathname={window.location.pathname}
				/>
			)}
			<Box sx={innerSxArray}>
				<AgGridReact
					{...nativeProps}
					{...(nativeProps.columnDefs
						? { columnDefs: guardedNativeColumnDefs }
						: hasColumnCapabilities
							? { columnDefs: combinedGridOptions.columnDefs }
							: {})}
					{...(mergedGetContextMenuItems ? { getContextMenuItems: mergedGetContextMenuItems } : {})}
					cellSelection={nativeProps.cellSelection ?? true}
					doesExternalFilterPass={doesExternalFilterPass}
					enterNavigatesVerticallyAfterEdit={nativeProps.enterNavigatesVerticallyAfterEdit ?? true}
					getRowId={getStubSafeRowId}
					gridOptions={combinedGridOptions}
					postProcessPopup={(params) => {
						filterFeedback.postProcessPopup(params);
						(nativeProps.postProcessPopup ?? gridOptions?.postProcessPopup)?.(params);
					}}
					isExternalFilterPresent={isExternalFilterPresent}
					localeText={nativeProps.localeText ?? localeText}
					onBodyScroll={debouncedSaveGridState}
					onCellFocused={saveGridState}
					onColumnMoved={saveGridState}
					onColumnResized={saveGridState}
					onColumnValueChanged={saveGridState}
					onColumnVisible={saveGridState}
					onFilterChanged={mergedOnFilterChanged}
					onFirstDataRendered={loadGridState}
					onGridReady={mergedOnGridReady}
					onSelectionChanged={(event) => {
						saveGridState();
						if (nativeProps.onSelectionChanged) {
							nativeProps.onSelectionChanged(event);
						}
					}}
					onSortChanged={(event) => {
						saveGridState();
						if (nativeProps.onSortChanged) {
							nativeProps.onSortChanged(event);
						}
					}}
					ref={gridRef}
					{...(serverSideRowModel ? {} : { rowData })}
					rowSelection={rowSelection}
					theme={theme}
				/>
				{filterFeedback.errorPortal}
				<GridLockOverlay isProcessing={isProcessing} />
			</Box>
			{showFilterBar && filterBarPlacement === "bottom" && (
				<MruFilterBar
					hasRibbon={false}
					columnDefs={(gridOptions?.columnDefs ?? []) as ColDef[]}
					gridRef={gridRef}
					mruFilters={mruFilters}
					onFilterApplied={updateFilterModel}
					onPausedChange={setFiltersPaused}
					pathname={window.location.pathname}
				/>
			)}
			{formattingPanelOpen && (
				<FormattingPanel
					columns={gridOptions?.columnDefs as ColDef[]}
					formattingPanelOpen={formattingPanelOpen}
					gridDataKey={gridDataKey}
					rules={formattingRules}
					setFormattingPanelOpen={setFormattingPanelOpen}
					setRules={setFormattingRules}
				/>
			)}
		</Box>
	);
};

const AgGridWithoutRibbon = <TData,>(
	props: AgGridWithoutRibbonProps<TData> & Partial<React.ComponentProps<typeof AgGridReact<TData>>>
) => {
	const pageFileExport = useContext(GridFileExportContext);
	const isServerSide = isServerSideRowModel(props.rowModelType, props.gridOptions);
	const source = isServerSide
		? getSsrmExportSource(props.serverSideDatasource ?? props.gridOptions?.serverSideDatasource)
		: undefined;
	const content = <AgGridWithoutRibbonContent {...props} showDefaultExport={!pageFileExport && !!source} />;
	if (pageFileExport || !source) return content;
	return (
		<DefaultServerSideFileExport gridDataKey={props.gridDataKey} gridRef={props.gridRef} source={source}>
			{content}
		</DefaultServerSideFileExport>
	);
};

export default AgGridWithoutRibbon;
